import uniqBy from 'lodash/uniqBy';
import type { ITrafficSituation } from '../models/traffic-situation.model';
import type { ILine } from '../models/line.model';
import type { IServiceJourney } from '../models/service-journey.model';
import type { TransportMode } from '../models/transport-mode.enum';
import { DateTime } from 'luxon';
import { TrafficSituationSeverity } from '../models/traffic-situation-severity.enum';

export class TrafficSituationHelper {
  private static get today(): DateTime {
    return DateTime.now().startOf('day');
  }

  private static get yesterday(): DateTime {
    return DateTime.now().startOf('day').minus({ days: 1 });
  }

  public static getDistinctLines(ts: ITrafficSituation): ILine[] {
    let result: ILine[] = [];

    if (ts.affectedLines) {
      result = result.concat(ts.affectedLines);
    }

    if (ts.affectedJourneys) {
      result = result.concat(ts.affectedJourneys.map((affectedJourney: IServiceJourney) => affectedJourney.line).filter((line) => line !== undefined) as ILine[]);
    }

    return uniqBy(result, (line) => line.gid);
  }

  public static affectsLineWithTransportMode(ts: ITrafficSituation, tm: TransportMode): boolean {
    return TrafficSituationHelper.getDistinctLines(ts).some((l) => l.transportMode === tm);
  }

  public static createLastUpdatedTimeText(trafficSituation: ITrafficSituation, translationFunc: (translationId: string) => string): string {
    const creationDate = trafficSituation.creationTime.startOf('day');
    if (creationDate.equals(TrafficSituationHelper.today)) {
      return `${translationFunc('trafficsituationspage.lastupdatedtimeprefix')} ${translationFunc('common.today').toLocaleLowerCase()} ${trafficSituation.creationTime.toFormat('HH:mm')}`;
    }
    if (creationDate.equals(TrafficSituationHelper.yesterday)) {
      return `${translationFunc('trafficsituationspage.lastupdatedtimeprefix')} ${translationFunc('common.yesterday').toLocaleLowerCase()} ${trafficSituation.creationTime.toFormat('HH:mm')}`;
    }
    return `${translationFunc('trafficsituationspage.lastupdatedtimeprefix')} ${trafficSituation.creationTime.toFormat('yyyy-MM-dd HH:mm')}`;
  }

  public static createValidityTimeText(trafficSituation: ITrafficSituation, translationFunc: (translationId: string) => string): string | undefined {
    let formatString = 'EEEE d MMMM';

    if ((trafficSituation.endTime && trafficSituation.startTime.year !== trafficSituation.endTime?.year) || (!trafficSituation.endTime && trafficSituation.startTime.year !== DateTime.now().year)) {
      formatString += ' yyyy';
    }

    const startTimeText = TrafficSituationHelper.isNewTrafficSituation(trafficSituation) ? translationFunc('common.today') : trafficSituation.startTime.toFormat(formatString) ?? '';

    const endTimeText = trafficSituation.endTime?.toFormat(formatString) ?? translationFunc('trafficsituationspage.untilfurthernotice');

    return `${startTimeText} - ${endTimeText}`;
  }

  public static getFirstSentenceInTitle(title: string): string {
    if (!title) return '';

    const firstDotIndex = title.indexOf('.');
    if (firstDotIndex === -1) {
      return title;
    }
    return title.substring(0, firstDotIndex + 1);
  }

  public static isCurrentTrafficSituation(trafficSituation: ITrafficSituation): boolean {
    return trafficSituation.startTime.startOf('day') <= TrafficSituationHelper.today && (!trafficSituation.endTime || trafficSituation.endTime.startOf('day') >= TrafficSituationHelper.today);
  }

  public static isNewTrafficSituation(trafficSituation: ITrafficSituation): boolean {
    return TrafficSituationHelper.isCurrentTrafficSituation(trafficSituation) ? trafficSituation.startTime > DateTime.now().minus({ hours: 24 }) : false;
  }

  public static isPlannedTrafficSituation(trafficSituation: ITrafficSituation): boolean {
    return trafficSituation.startTime.startOf('day') > TrafficSituationHelper.today;
  }

  public static getTrafficSituationSeverityEnum(trafficSituationSeverity: string): TrafficSituationSeverity {
    const normalized = trafficSituationSeverity?.toLowerCase?.();
    if (normalized === 'severe') {
      return TrafficSituationSeverity.severe;
    } else if (normalized === 'normal') {
      return TrafficSituationSeverity.normal;
    } else if (normalized === 'slight') {
      return TrafficSituationSeverity.slight;
    }
    return TrafficSituationSeverity.normal;
  }
}
